import React, { useState } from "react";
import { Check, Copy, Send } from "lucide-react";
import { Tooltip } from "@heroui/react";
import MessageContent from "./MessageContent";

const EmailDraftCard = ({ draft }) => {
  const [copied, setCopied] = useState(false);
  if (!draft) return null;

  const copyDraft = async () => {
    const headers = [
      `To: ${(draft.to || []).join(", ")}`,
      ...(draft.cc?.length ? [`Cc: ${draft.cc.join(", ")}`] : []),
      `Subject: ${draft.subject || ""}`,
    ];
    try {
      await navigator.clipboard.writeText(
        `${headers.join("\n")}\n\n${draft.body_markdown || ""}`,
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  // ponytail: display-only until chat email sending is explicitly approved
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Email draft
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={copyDraft}
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-gray-500 dark:text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-200"
            aria-label="Copy email draft"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy"}
          </button>
          <Tooltip content="Sending from chat is not configured yet." placement="top">
            <span tabIndex={0} aria-label="Sending from chat is not configured yet.">
              <button
                type="button"
                disabled
                className="inline-flex h-8 cursor-not-allowed items-center gap-1.5 rounded-full bg-secondary px-3 text-xs font-semibold text-white opacity-50"
              >
                <Send size={13} />
                Send
              </button>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
        <div className="grid grid-cols-[72px_1fr] gap-3 px-4 py-3">
          <span className="text-gray-400 dark:text-gray-400 font-medium">To</span>
          <span className="break-words text-gray-700 dark:text-gray-300">
            {(draft.to || []).join(", ") || "Recipient not set"}
          </span>
        </div>
        {draft.cc?.length > 0 && (
          <div className="grid grid-cols-[72px_1fr] gap-3 px-4 py-3">
            <span className="text-gray-400 dark:text-gray-400 font-medium">Cc</span>
            <span className="break-words text-gray-700 dark:text-gray-300">{draft.cc.join(", ")}</span>
          </div>
        )}
        <div className="grid grid-cols-[72px_1fr] gap-3 px-4 py-3">
          <span className="text-gray-400 dark:text-gray-400 font-medium">Subject</span>
          <span className="font-medium text-gray-800 dark:text-gray-100">{draft.subject}</span>
        </div>
        <div className="px-4 py-4 text-gray-700 dark:text-gray-300">
          <MessageContent
            markdown
            id={`email-draft-${draft.subject || "untitled"}`}
            className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2"
          >
            {draft.body_markdown}
          </MessageContent>
        </div>
      </div>
    </div>
  );
};

export default EmailDraftCard;
